import { getFormIdFromUrl } from "./../../helpers/getUrlParams";
import { api } from "../../services/api";
import { ApiDynamicPaths } from "../../services/api/paths";
import {
    GetZapierAuthURLResponse,
    GetZapierZapsResponse,
    HubspotAuthURLResponse,
    GetHubspotDataResponse,
    GetHubspotStatusResponse,
    UpdateHubspotPropertyResponse,
    UpdateHubspotPropertyRequest,
    GetHubspotTablesRequest,
    GetHubspotTablesResponse,
    GetGoogleSheetsAuthURLResponse,
    GetGoogleSheetsResponse,
    UpdateGoogleSheetsConfigRequest,
    DisconnectIntegrationResponse,
    UpdateGoogleSheetsConfigResponse,
    GetGoogleSheetWorksheetResponse,
    GetIntegrationStatusesResponse,
    GetMailchimpAuthURLResponse,
    UpdateMailchimpConfigRequest,
    UpdateMailchimpConfigResponse,
    GetMailchimpListsResponse,
    GetMailchimpSelectedFields,
} from "./integration.service.d";

export class IntegrationService {
    /**
     * Get Zapier Auth URL
     *
     */
    static async getZapierAuthURL(formId: string): Promise<GetZapierAuthURLResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).ZAPIER_AUTH_URL);
    }

    /**
     * Get Zapier Tokens
     *
     */
    static async getZapierTokens(formId: string) {
        return api.get(ApiDynamicPaths({ form_id: formId }).ZAPIER_TOKENS);
    }

    /**
     * Get Zapier Zaps
     *
     */
    static async getZapierZaps(formId: string): Promise<GetZapierZapsResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GET_ZAPS);
    }
    /**
     * Disconnect Zapier
     *
     */
    static async disconnectZapier(formId: string) {
        return api.post(ApiDynamicPaths({ form_id: formId }).DISCONNECT_ZAPIER, {
            app: "form",
            platform: "platform",
            app_id: formId,
            service: "zapier_integration",
        });
    }

    /**
     * Disconnect an integration
     *
     * @param type {number}
     */
    static async disconnectIntegration(type: string): Promise<DisconnectIntegrationResponse> {
        return api.put(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).DISCONNECT_INTEGRATION, {
            type,
        });
    }

    /**
     * Get Hubspot Auth URL
     *
     */

    static async getHubspotAuthURL(formId: string): Promise<HubspotAuthURLResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).HUBSPOT_AUTH_URL);
    }

    /**
     * Get Hubspot Data
     *
     */

    static async getHubspotData(formId: string): Promise<GetHubspotDataResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GET_HUBSPOT_DATA);
    }

    /**
     * Get Hubspot Status
     *
     */
    static async getHubspotStatus(formId: string): Promise<GetHubspotStatusResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GET_HUBSPOT_STATUS);
    }

    /**
     * Update Hubspot Property
     *
     */
    static async updateHubspotProperty(
        formId: string,
        data: UpdateHubspotPropertyRequest,
    ): Promise<UpdateHubspotPropertyResponse> {
        return api.put(ApiDynamicPaths({ form_id: formId }).UPDATE_HUBSPOT_PROPERTY, data);
    }

    /**
     * Get Hubspot Tables
     *
     */
    static async getHubspotTables(formId: string, table: GetHubspotTablesRequest): Promise<GetHubspotTablesResponse> {
        return api.get(`${ApiDynamicPaths({ form_id: formId }).GET_HUBSPOT_TABLES}?table=${table.table}`);
    }

    /**
     * Get Google Sheets Auth URL
     *
     */
    static async getGoogleSheetsAuthURL(formId: string): Promise<GetGoogleSheetsAuthURLResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GoogleSheets_AUTH_URL);
    }

    /**
     * Get Google Sheets
     * @param formId
     */
    static async getGoogleSheets(formId: string): Promise<GetGoogleSheetsResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GET_GOOGLE_SHEETS);
    }

    /**
     * Get Google Sheets worksheets
     * @param formId
     *  @param sheetId
     */
    static async getGoogleSheetsWorksheets(formId: string, sheetId: string): Promise<GetGoogleSheetWorksheetResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId, sheetId }).GET_GOOGLE_WORKSHEETS);
    }

    /**
     * Update Google Sheets Configuration
     *
     * @param formId {string}
     * @param data
     */
    static async updateGoogleSheetsConfig(
        formId: string,
        data: UpdateGoogleSheetsConfigRequest,
    ): Promise<UpdateGoogleSheetsConfigResponse> {
        return api.put(ApiDynamicPaths({ form_id: formId }).UPDATE_GOOGLE_SHEETS_CONFIG, data);
    }

    /**
     * Get Integration Statuses
     *
     * @param formId {string}
     */
    static async getIntegrationStatuses(
        formId: string,
        params?: { method?: string },
    ): Promise<GetIntegrationStatusesResponse> {
        const basePath = ApiDynamicPaths({ form_id: formId }).GET_INTEGRATION_STATUSES;
        const queryString = params?.method ? `?method=${params.method}` : "";

        return api.get(`${basePath}${queryString}`);
    }

    /**
     * Get Mailchimp Auth URL
     *
     */
    static async getMailchimpAuthURL(formId: string): Promise<GetMailchimpAuthURLResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).MAILCHIMP_AUTH_URL);
    }

    /**
     * Update Mailchimp Configuration
     *
     * @param formId {string}
     * @param data
     */
    static async updateMailchimpConfig(
        formId: string,
        data: UpdateMailchimpConfigRequest,
    ): Promise<UpdateMailchimpConfigResponse> {
        return api.put(ApiDynamicPaths({ form_id: formId }).UPDATE_MAILCHIMP_CONFIG, data);
    }

    /**
     * Get Mailchimp Lists
     *
     * @param formId
     */

    static async getMailchimpLists(formId: string): Promise<GetMailchimpListsResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GET_MAILCHIMP_LISTS);
    }

    /**
     * Get Mailchimp Selected Fields
     *
     * @param formId
     */

    static async getMailchimpSelectedFields(formId: string, listId: string): Promise<GetMailchimpSelectedFields> {
        return api.post(ApiDynamicPaths({ form_id: formId }).GET_MAILCHIMP_SELECTED_FIELDS, { id: listId });
    }
}
